import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

import {
  ArrowLeft,
  CaretRight,
  FileText
} from '@phosphor-icons/react';
const CATEGORY_INFO = {
  'getting-started': {
    name: 'Getting Started',
    description: 'Learn the basics of Man & Machine'
  },
  'seats-billing': {
    name: 'Seats & Billing',
    description: 'Understanding seats, plans, and billing'
  },
  'agents': {
    name: 'AI Agents',
    description: 'Creating, deploying, and managing agents'
  },
  'workflows': {
    name: 'Workflows',
    description: 'Automating multi-step processes'
  },
  'integrations': {
    name: 'Integrations',
    description: 'Connecting external services'
  },
  'troubleshooting': {
    name: 'Troubleshooting',
    description: 'Common issues and solutions'
  }
};

const HelpCategory = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetchArticles();
  }, [category]);

  const fetchArticles = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/help/categories/${category}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      } else {
        navigate('/help');
      }
    } catch (error) {
      console.error('Failed to fetch articles:', error);
      navigate('/help');
    } finally {
      setLoading(false);
    }
  };

  const categoryInfo = CATEGORY_INFO[category] || {
    name: 'Help Articles',
    description: ''
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{categoryInfo.name} - Help Centre - Man & Machine</title>
        <meta name="description" content={categoryInfo.description} />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] pt-24 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/help')}
            className="mb-6 flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-gray-300 hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Centre
          </button>

          {/* Category Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{categoryInfo.name}</h1>
            <p className="text-lg text-gray-400">{categoryInfo.description}</p>
            <div className="mt-4 text-sm text-gray-500">
              {articles.length} {articles.length === 1 ? 'article' : 'articles'}
            </div>
          </div>

          {/* Articles List */}
          {articles.length > 0 ? (
            <div className="space-y-4">
              {articles.map((article) => (
                <div
                  key={article.id}
                  className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.1)] hover:border-blue-500/50 transition-all cursor-pointer"
                  onClick={() => navigate(`/help/articles/${article.slug}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400 mt-1">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-white mb-2">{article.title}</h3>
                        {article.content && (
                          <p className="text-gray-400 line-clamp-2">
                            {article.content.split('\n').find(line => 
                              line.trim() && !line.startsWith('#')
                            ) || ''}
                          </p>
                        )}
                      </div>
                    </div>
                    <CaretRight className="w-5 h-5 text-gray-500 flex-shrink-0 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="glass rounded-2xl p-12 border border-[rgba(255,255,255,0.1)] text-center">
              <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">No articles in this category yet.</p>
              <button
                onClick={() => navigate('/help')}
                className="px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
              >
                Browse all categories
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default HelpCategory;
